import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface ChainLockDialogData {
  chainNumber: string;
  matchRunNumber: string;
  action: 'lock' | 'unlock' | 'lock_up_to_waitlist' | 'lock_merged_chain' | 'unlock_entire_chain';
  waitlistEnisNumber?: string; // ENIS of the waitlist patient
}

export interface ChainLockDialogResult {
  confirmed: boolean;
  comment?: string;
}

@Component({
  selector: 'app-chain-lock-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, FormsModule],
  template: `
    <h2 mat-dialog-title>{{ getTitle() }}</h2>
    <mat-dialog-content>
      <!-- Regular lock -->
      <p *ngIf="data.action === 'lock'">
        Locking this chain ensures it persists in future Matchruns.
        After locking, you can choose to 'Unlock' or 'Select for Transplantation'.
      </p>
      <p *ngIf="data.action === 'lock'" class="note-warning">
        Note: All participants in this chain receive a permanent 'Locked' status in the Participants screen and cannot be modified until unlocked.
      </p>

      <!-- Lock up to waitlist -->
      <p *ngIf="data.action === 'lock_up_to_waitlist'">
        This action locks the chain up to the waitlist patient ({{ data.waitlistEnisNumber }}).
        All participants up to and including the donor paired with the waitlist patient will be locked.
        The waitlist patients remain unlocked and can be rematched in the next algorithm run.
      </p>
      <p *ngIf="data.action === 'lock_up_to_waitlist'" class="note-info">
        The partially locked donor and waitlist recipients will be included in the next algorithm run.
        If the donor appears in a new chain, it will be automatically merged with the locked portion.
      </p>

      <!-- Lock merged chain -->
      <p *ngIf="data.action === 'lock_merged_chain'">
        Dit locked de gehele samengevoegde keten (zowel het gelockte deel als het nieuwe deel van het algoritme).
        De keten blijft volledig gelocked in toekomstige Matchruns.
      </p>

      <!-- Unlock entire chain -->
      <p *ngIf="data.action === 'unlock_entire_chain'">
        Unlocking this chain removes the guarantee that the chain will return in the same form after the next Matchrun.
      </p>
      <p *ngIf="data.action === 'unlock_entire_chain'" class="note-warning">
        Note: All participants in this chain will revert to ‘Unlocked' status, allowing their status to be changed (e.g., to inactive or remove them from the program).’
      </p>

      <!-- Regular unlock -->
      <p *ngIf="data.action === 'unlock'">
        Unlocking this chain removes the guarantee that the chain will return in the same form after the next Matchrun.
      </p>
      <p *ngIf="data.action === 'unlock'" class="note-warning">
        Note: All participants in this chain will revert to 'Unlocked' status, allowing their status to be changed (e.g., to inactive or remove them from the program).
      </p>

      <!-- Comment field for locking actions -->
      <div *ngIf="data.action === 'lock' || data.action === 'lock_up_to_waitlist' || data.action === 'lock_merged_chain'" class="lock-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Comment (optional)</mat-label>
          <textarea matInput [(ngModel)]="comment" rows="3" placeholder="Add any notes or comments..."></textarea>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">CANCEL</button>
      <button mat-button color="warn" (click)="onConfirm()">
        {{ getButtonText() }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] {
      font-size: 24px !important;
      font-weight: 500 !important;
      margin: 0 0 20px 0 !important;
      color: rgba(0, 0, 0, 0.87) !important;
    }
    mat-dialog-content {
      max-width: 600px;
      min-width: 500px;
    }
    mat-dialog-content p {
      margin-bottom: 16px;
      font-size: 14px;
    }
    .note-warning {
      color: #f44336 !important;
      font-weight: 500;
    }
    .note-info {
      color: #1976d2 !important;
      font-weight: 500;
    }
    .lock-form {
      margin: 24px 0;
    }
    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }
  `]
})
export class ChainLockDialogComponent {
  comment: string = '';

  constructor(
    public dialogRef: MatDialogRef<ChainLockDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChainLockDialogData
  ) {}

  getTitle(): string {
    switch (this.data.action) {
      case 'lock':
        return `Confirm chain lock ${this.data.matchRunNumber}:${this.data.chainNumber}`;
      case 'unlock':
        return `Confirm chain unlock ${this.data.matchRunNumber}:${this.data.chainNumber}`;
      case 'lock_up_to_waitlist':
        return `Lock chain up to waitlist ${this.data.matchRunNumber}:${this.data.chainNumber}`;
      case 'lock_merged_chain':
        return `Lock entire merged chain ${this.data.matchRunNumber}:${this.data.chainNumber}`;
      case 'unlock_entire_chain':
        return `Confirm chain unlock ${this.data.matchRunNumber}:${this.data.chainNumber}`;
      default:
        return `Confirm action`;
    }
  }

  getButtonText(): string {
    switch (this.data.action) {
      case 'lock':
        return 'LOCK CHAIN';
      case 'unlock':
        return 'UNLOCK CHAIN';
      case 'lock_up_to_waitlist':
        return 'LOCK UP TO WAITLIST';
      case 'lock_merged_chain':
        return 'LOCK ENTIRE CHAIN';
      case 'unlock_entire_chain':
        return 'UNLOCK CHAIN';
      default:
        return 'CONFIRM';
    }
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  onConfirm(): void {
    const needsComment = ['lock', 'lock_up_to_waitlist', 'lock_merged_chain'].includes(this.data.action);

    if (needsComment) {
      this.dialogRef.close({
        confirmed: true,
        comment: this.comment || undefined
      });
    } else {
      this.dialogRef.close({ confirmed: true });
    }
  }
}
